import ArrowRightIcon from "@assets/svg/arrow-right.svg?react"
import ArrowLeftIcon from "@assets/svg/arrow.svg?react"
import { Carousel, type Embla } from "@mantine/carousel"
import { ActionIcon, Anchor, Group, Text } from "@mantine/core"
import { IconBell } from "@tabler/icons-react"
import { useCallback, useEffect, useState, useRef } from "react"
import styles from "./AlertBar.module.css"
import carouselStyles from "./Carousel.module.css"
import { useRedirect } from "@hooks/useRedirect"
import { eventTrackingItems } from "@utils/eventTracking"
import { useAEMAlertsData } from "@AEM/hooks/useAEMAlertsData"
import type { AEMAlertItem } from "@AEM/models/AEMResponse.model"

interface AlertBarProps {
	className: string
	onClose: () => void
}

export function AlertBar({ className }: AlertBarProps) {
	const [embla, setEmbla] = useState<Embla | null>(null)
	const [selected, setSelected] = useState(1)
	const numSlides = embla ? embla.slideNodes().length : 0
	const firstAlertRef = useRef<HTMLButtonElement>(null)
	const { handleRedirect } = useRedirect()
	const { alertsData, isLoading } = useAEMAlertsData()

	const handleSelect = useCallback(() => {
		if (!embla) return
		const slide = embla.selectedScrollSnap()
		setSelected(slide + 1)
	}, [embla])

	const today = new Date()
	const filteredAlerts = alertsData?.filter((alert: AEMAlertItem) => {
		const startDate = new Date(alert.start)
		const endDate = new Date(alert.end)
		return startDate <= today && endDate >= today
	})

	useEffect(() => {
		if (embla) {
			embla.on("select", handleSelect)

			return () => {
				embla.off("select", handleSelect)
			}
		}

		return undefined
	}, [embla, handleSelect])

	useEffect(() => {
		if (filteredAlerts && filteredAlerts.length > 0) {
			if (firstAlertRef.current) {
				setTimeout(() => {
					firstAlertRef.current?.focus()
				}, 500)
			}
		}
	}, [])

	if (isLoading || !alertsData?.length || !filteredAlerts?.length) {
		return null
	}

	const handleAlertToggle = (action?: "link" | "close") => {
		eventTrackingItems({
			event_name: "alert_bar_click",
			action: action,
			interactionType: "Alert Bar Content",
			category: "Alert Bar Navigation",
			event211: 1
		})
	}

	const formatAlertMessage = (alert: AEMAlertItem) => {
		return alert.alertTitle?.plaintext || ""
	}

	const handleAlertsClick = (alert: AEMAlertItem) => {
		eventTrackingItems({
			event_name: "click",
			link_url: "/alerts",
			category: "click",
			interactionType: "alert_click",
			navLinkText: alert.alertTitle?.plaintext ?? ""
		})
		handleRedirect({
			link: "/alerts",
			type: "route",
			appId: alert?.appId ?? undefined
		})
	}

	return (
		<Group className={className} wrap="nowrap">
			<Group gap="xs" align="center" wrap="nowrap">
				<IconBell
					size={16}
					color="var(--text-color-primary)"
					style={{ flexShrink: 0 }}
				/>
				<Carousel
					classNames={carouselStyles}
					withControls={false}
					getEmblaApi={setEmbla}
					loop={true}
					slideGap="sm"
					align="start"
				>
					{filteredAlerts?.map((alert: AEMAlertItem, index: number) => (
						<Carousel.Slide
							key={alert.appId || alert.alertTitle?.plaintext || Math.random()}
						>
							<Anchor
								ref={index === 0 ? firstAlertRef : undefined}
								component="button"
								underline="hover"
								onClick={() => handleAlertsClick(alert)}
							>
								<Text
									className={styles.alertText}
									role="alert"
									aria-live="assertive"
									aria-atomic="true"
									aria-label={`Alert: ${formatAlertMessage(alert)}`}
								>
									{formatAlertMessage(alert)}
								</Text>
							</Anchor>
						</Carousel.Slide>
					))}
				</Carousel>
			</Group>

			<Group gap="xs" wrap="nowrap">
				{numSlides > 1 && (
					<Group gap="xs" wrap="nowrap">
						<ActionIcon
							variant="transparent"
							className={styles.carouselArrow}
							onClick={() => {
								embla?.scrollPrev()
								handleAlertToggle("link")
							}}
							data-qa="alert.button.previous"
						>
							<ArrowLeftIcon />
						</ActionIcon>
						<Text className={styles.carouselPaginationText}>
							{selected} of {numSlides}
						</Text>
						<ActionIcon
							variant="transparent"
							className={styles.carouselArrow}
							onClick={() => {
								embla?.scrollNext()
								handleAlertToggle("link")
							}}
							data-qa="alert.button.next"
						>
							<ArrowRightIcon />
						</ActionIcon>
					</Group>
				)}
			</Group>
		</Group>
	)
}
